import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import {
    HistoryLogDetail,
    HistoryLogItem,
    HistoryLogSliceModel,
    HistoryLogs
} from "./history-log-interfaces";
import { Guid } from "guid-typescript";
import { API_BASE_URL as baseUri } from "config/env";

const initalValue: HistoryLogSliceModel = {
    statusCode: '',
    message: '',
    isLoading: false,
    historyLogs: [],
    historyLogDetail: null
}

export const GetAllHistoryLogs = createAsyncThunk<HistoryLogItem[], void, { rejectValue: RejectedValue }>(
    'Slice/HistoryLogSlice/GetAllHistoryLogs',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/historylog/getall', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HistoryLogItem[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetHistoryLog = createAsyncThunk<HistoryLogDetail, Guid, { rejectValue: RejectedValue }>(
    'Slice/HistoryLogSlice/GetHistoryLog',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/historylog/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as HistoryLogDetail;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const HistoryLogSlice = createSlice({
    name: "HistoryLogSlice",
    initialState: initalValue,
    reducers: {
        resetHistoryLogSlice: (state) => {
            state.statusCode = '';
            state.message = '';
            state.isLoading = false;
            state.historyLogs = [];
            state.historyLogDetail = null;
        },
        resetHistoryLogDetail: (state) => {
            state.historyLogDetail = null;
        }
    },
    extraReducers(builder) {
        //Get all history logs
        builder.addCase(GetAllHistoryLogs.fulfilled, (state, action: PayloadAction<HistoryLogItem[]>) => {
            state.isLoading = false;
            state.message = '';
            state.historyLogs = action.payload;
        })
            .addCase(GetAllHistoryLogs.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllHistoryLogs.pending, state => {
                state.isLoading = true;
            })

        //Get history log detail
        builder.addCase(GetHistoryLog.fulfilled, (state, action: PayloadAction<HistoryLogDetail>) => {
            state.isLoading = false;
            state.message = '';
            state.historyLogDetail = action.payload;
        })
            .addCase(GetHistoryLog.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetHistoryLog.pending, state => {
                state.isLoading = true;
            })
    }

})

export const { resetHistoryLogSlice, resetHistoryLogDetail } = HistoryLogSlice.actions;
export default HistoryLogSlice.reducer;